import { GraduationCap, Pill, Siren, Briefcase, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToSection } from "../../config/site";

const PROJECTS = [
  { icon: GraduationCap, title: "School Management SaaS", category: "SaaS / Education / ERP", tone: "gold" },
  { icon: Pill, title: "Pharmacy Mobile Application", category: "Mobile App / Healthcare", tone: "magenta" },
  { icon: Siren, title: "Safety & Emergency Mobile App", category: "Mobile App / Safety", tone: "navy" },
  { icon: Briefcase, title: "Custom Business Software", category: "Software / SaaS", tone: "gold" },
];

const TONES = {
  gold: { bg: "bg-gold-pale", text: "text-gold", bar: "from-gold to-gold-light" },
  magenta: { bg: "bg-magenta-pale", text: "text-magenta", bar: "from-magenta to-magenta-dark" },
  navy: { bg: "bg-slate-100", text: "text-navy", bar: "from-navy to-slate-600" },
};

export const Portfolio = () => (
  <section id="solutions" className="bg-white py-24 sm:py-32" data-testid="portfolio-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <Eyebrow>Solutions Showcase</Eyebrow>
          <h2 className="mt-5 font-jakarta text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="portfolio-heading">
            Selected Digital Solutions
          </h2>
        </div>
        <button
          onClick={() => scrollToSection("#contact")}
          data-testid="portfolio-view-work-cta"
          className="group inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 font-jakarta text-[13px] font-semibold text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          View Our Work
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PROJECTS.map((p, i) => {
          const t = TONES[p.tone];
          return (
            <Reveal key={p.title} delay={(i % 4) * 0.08}>
              <article
                className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1.5 hover:border-gold/50 hover:shadow-[0_28px_50px_-24px_rgba(17,24,39,0.25)]"
                data-testid={`portfolio-card-${i + 1}`}
              >
                {/* abstract product visual */}
                <div className={`relative h-40 overflow-hidden ${t.bg}`}>
                  <div className={`absolute inset-x-6 top-8 rounded-t-lg bg-navy p-3 shadow-xl transition-transform duration-500 group-hover:-translate-y-1.5`}>
                    <div className="flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                      <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                    </div>
                    <div className="mt-2.5 space-y-1.5">
                      <div className={`h-1.5 w-2/3 rounded bg-gradient-to-r ${t.bar}`} />
                      <div className="h-1.5 w-full rounded bg-white/15" />
                      <div className="h-1.5 w-4/5 rounded bg-white/15" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 ${t.text}`}>
                    <p.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-jakarta text-[16px] font-bold leading-snug text-navy">{p.title}</h3>
                  <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500">{p.category}</p>
                  <span className="mt-4 inline-flex w-fit items-center rounded-full border border-dashed border-slate-300 px-3 py-1 font-mono2 text-[9.5px] uppercase tracking-[0.18em] text-slate-400" data-testid={`portfolio-label-${i + 1}`}>
                    Concept showcase
                  </span>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.15}>
        <p className="mt-8 max-w-2xl font-inter text-[12.5px] leading-relaxed text-slate-400" data-testid="portfolio-disclaimer">
          The cards above are representative solution concepts that illustrate the type of products RCS designs and
          builds. Client project details are shared during a consultation.
        </p>
      </Reveal>
    </div>
  </section>
);
